import React from "react";

interface SlideContent {
  question?: string;
  instructions?: string;
  thinkingEmoji?: string;
  discussEmojis?: string[];
  bgColor?: string;
  showBottomIndicators?: boolean;
  showBackgroundPattern?: boolean;
  onEdit?: (content: SlideContent) => void;
}

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const QuestionPromptSlide = {
  id: "question-prompt-slide",
  name: "Question / Prompt Slide",
  fields: [
    {
      id: "question",
      label: "Main Question",
      type: "text",
      defaultValue: "",
    },
    {
      id: "instructions",
      label: "Prompt Instructions",
      type: "text",
      defaultValue: "",
    },
    {
      id: "thinkingEmoji",
      label: "Thinking Emoji",
      type: "text",
      defaultValue: "🤔",
    },
    {
      id: "discussEmojis",
      label: "Discussion Emojis",
      type: "text",
      defaultValue: JSON.stringify(["🗣️", "👥", "💭"]),
    },
    {
      id: "showBottomIndicators",
      label: "Show Bottom Indicators",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "showBackgroundPattern",
      label: "Show Background Pattern",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #4c1d95 0%, #5b21b6 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      question = "",
      instructions = "",
      thinkingEmoji = "🤔",
      discussEmojis = ["🗣️", "👥", "💭"],
      showBottomIndicators = true,
      showBackgroundPattern = true,
      bgColor = "linear-gradient(135deg, #4c1d95 0%, #5b21b6 100%)",
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleEmojiArrayEdit = (index: number) => (value: string) => {
      const newEmojis = [...discussEmojis];
      newEmojis[index] = value;
      handleEdit("discussEmojis")(JSON.stringify(newEmojis));
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        {showBackgroundPattern && (
          <div className="absolute inset-0">
            <div className="absolute inset-0 opacity-10">
              <div className="grid grid-cols-12 grid-rows-12">
                {[...Array(144)].map((_, i) => (
                  <div
                    key={i}
                    className="w-full h-full border-r border-t border-white/5"
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-8">
          <div className="max-w-4xl w-full">
            {/* Question Mark Icon */}
            <div className="text-center mb-8">
              <EditableDiv
                text={thinkingEmoji}
                onEdit={handleEdit("thinkingEmoji")}
                className="text-7xl transform hover:scale-110 transition-transform inline-block"
              />
            </div>

            {/* Question */}
            <div className="text-center mb-12">
              <h2 className="text-5xl font-black leading-tight relative">
                <EditableDiv
                  text={question}
                  onEdit={handleEdit("question")}
                  className="relative text-violet-200"
                />
              </h2>
            </div>

            {/* Instructions Box */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border-l-4 border-violet-400">
              <div className="flex items-start gap-4">
                <span className="text-3xl">💡</span>
                <EditableDiv
                  text={instructions}
                  onEdit={handleEdit("instructions")}
                  className="text-xl text-violet-100/90 leading-relaxed"
                />
              </div>
            </div>

            {/* Bottom Indicators */}
            {showBottomIndicators && (
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-2 text-violet-300/50">
                {discussEmojis.map((emoji, idx) => (
                  <React.Fragment key={idx}>
                    <EditableDiv
                      text={emoji}
                      onEdit={handleEmojiArrayEdit(idx)}
                      className="transform hover:scale-110 transition-transform"
                    />
                    {idx < discussEmojis.length - 1 && <span>|</span>}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
};

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default QuestionPromptSlide;
